import { useEffect, useId, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { Ellipsis } from 'lucide-react'
import { focusableIn } from '../../platform/a11y/focusable'
import { Icon } from '../../platform/icons/Icon'
import { Button } from '../../platform/primitives/Button'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { canMarkPaid, canPaySkipped, canSkip, payPeriodOf, type LocalDate } from './recurringRules'
import type { Bill, Entry } from './recurringWire'
import { useAnchoredPlacement } from './useAnchoredPlacement'

/** The verbs Task 7 wires (S9, S10). Mark paid opens a sheet and is separate. */
export type RowAction = 'skip' | 'pause' | 'resume' | 'delete'

/** Why a paused bill's Skip is disabled. Adjacent text, tied to the item by `aria-describedby`. */
export const SKIP_PAUSED_REASON = 'Resume this bill to skip a payment.'

type Item = { key: string; label: string; run: () => void; disabledReason?: string; lifecycle?: boolean }

/**
 * A row's visible actions menu (D7: no swipe, a menu at every width), plus the
 * Overdue row's inline Mark paid at ≥ tablet.
 *
 * **Items appear only once something handles them.** Mark paid… is Task 6's;
 * Skip this one, Pause / Resume and Delete render when a caller supplies
 * `onAction` (Task 7). A menu item that does nothing is worse than an absent
 * one: it looks like a broken button. Mark paid… and Skip are absent on a paused
 * bill (S9: a paused bill cannot be skipped; nothing is owed while paused).
 * Mark paid… (and the inline button) appear only where `canMarkPaid` says —
 * the one row whose period the server will actually pay (Ruling 34) — or where
 * `canPaySkipped` does (#826): a skipped row, paid by its own date. The opener
 * is handed that period (`payPeriodOf`), so the sheet and the offer agree.
 *
 * **Hand-rolled ARIA menu button, the repo's fourth** (`RoleMenu`,
 * `MemberActionsMenu`, `OwnerMenu`): Enter or Space opens it onto the first
 * item, ArrowUp/ArrowDown move with wrap, Home/End jump, Escape and an outside
 * click close it and return focus to the button. Extracting a shared primitive
 * is overdue and is flagged in the Task 6 report rather than done here.
 *
 * **The inline Mark paid is `outline`, never lime** (rule 9): `/recurring`'s
 * primary action is + Add recurring. Its accessible name begins with its visible
 * text and adds the bill, because every Overdue row has one.
 */
export function RowActions({
  bill,
  entry,
  onOpenSheet,
  onAction,
  busy = false,
  inline = false,
}: {
  bill: Bill
  entry?: Entry
  onOpenSheet: (kind: 'markPaid', period: LocalDate | null) => void
  onAction?: (kind: RowAction) => void
  /** A lifecycle write for this bill is in flight: its verbs read aria-disabled and do nothing. */
  busy?: boolean
  inline?: boolean
}) {
  const [open, setOpen] = useState(false)
  // Where the panel is portalled: the enclosing dialog when the row sits in
  // one (the phone calendar's day sheet), so the dialog's focus trap and inert
  // sweep still count it as inside; otherwise <body>. Chosen when it opens.
  const [host, setHost] = useState<HTMLElement | null>(null)
  const phone = useBreakpoint() === 'phone'
  const rootRef = useRef<HTMLSpanElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([])
  const idBase = useId()
  const reasonIdOf = (key: string) => `${idBase}-${key}-reason`

  const close = () => {
    setOpen(false)
    // Focus is on an item about to unmount; hand it back first, so a sheet
    // opened next records the button as the place to restore focus to.
    triggerRef.current?.focus()
  }

  const items: Item[] = []
  const payable = canMarkPaid(bill, entry) || canPaySkipped(bill, entry)
  if (payable) items.push({ key: 'markPaid', label: 'Mark paid…', run: () => onOpenSheet('markPaid', payPeriodOf(bill, entry)) })
  if (onAction) {
    if (canSkip(bill, entry)) items.push({ key: 'skip', label: 'Skip this one', run: () => onAction('skip'), lifecycle: true })
    else if (entry && bill.isPaused) {
      items.push({ key: 'skip', label: 'Skip this one', run: () => undefined, disabledReason: SKIP_PAUSED_REASON })
    }
    items.push(
      bill.isPaused
        ? { key: 'resume', label: 'Resume bill', run: () => onAction('resume'), lifecycle: true }
        : { key: 'pause', label: 'Pause bill', run: () => onAction('pause'), lifecycle: true },
    )
    items.push({ key: 'delete', label: 'Delete bill', run: () => onAction('delete'), lifecycle: true })
  }

  itemRefs.current.length = items.length

  useEffect(() => {
    if (!open) return
    function onPointerDown(event: MouseEvent) {
      const target = event.target as Node
      // The panel is portalled out of the root, so "outside" means outside both.
      if (rootRef.current?.contains(target) || panelRef.current?.contains(target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open])

  /**
   * The panel is portalled to `<body>` and `position: fixed`, because every
   * place a row lives clips: the list's DataGrid table scrolls (`overflow:
   * auto`) and its section Panel has `overflow: hidden` for its corners. Inside
   * them the open menu showed one item of four, and focusing that item scrolled
   * the clipping table so the row's own name slid under the section band.
   * `useAnchoredPlacement` keeps the fixed coordinates current. Only the
   * coordinates travel inline, as custom properties; the skin stays in
   * recurring.css.
   */
  const { place, placed } = useAnchoredPlacement(triggerRef, panelRef, open, phone)

  // Focus moves in once the panel is PLACED, not merely open. Until it has
  // coordinates it is `visibility: hidden` (so it never paints at the corner),
  // and a hidden element cannot take focus: focusing on `open` alone left focus
  // on the trigger, so arrows and Escape went nowhere. jsdom ignores
  // visibility, so only a real browser showed it. `placed` flips once per open
  // (place resets to null on close), so a re-place on scroll never steals focus.
  useEffect(() => {
    // preventScroll: the panel is fixed and already in view; letting focus
    // scroll anything would move the row out from under its own menu.
    if (open && placed) itemRefs.current[0]?.focus({ preventScroll: true })
  }, [open, placed])

  function onMenuKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const count = items.length
    const at = itemRefs.current.findIndex((el) => el === document.activeElement)
    const go = (i: number) => {
      event.preventDefault()
      itemRefs.current[(i + count) % count]?.focus()
    }
    if (event.key === 'ArrowDown') go(at + 1)
    else if (event.key === 'ArrowUp') go(at - 1)
    else if (event.key === 'Home') go(0)
    else if (event.key === 'End') go(count - 1)
    else if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      close()
    } else if (event.key === 'Tab') {
      // The panel sits at the end of its host, so the browser's own Tab would
      // leave the page. Keep the order it had when the panel followed the
      // trigger in the DOM: Shift+Tab lands on the trigger, Tab on whatever
      // follows the menu. The order is built from the HOST, not <body>: in the
      // phone day sheet the host is the dialog, and the page behind it is
      // inert, so a body-wide order could hand focus to the page.
      event.preventDefault()
      setOpen(false)
      const trigger = triggerRef.current
      if (!trigger) return
      if (event.shiftKey) return trigger.focus()
      const order = focusableIn(host ?? document.body).filter((el) => el.tabIndex >= 0 && !panelRef.current?.contains(el))
      ;(order[order.indexOf(trigger) + 1] ?? trigger).focus()
    }
  }

  const label = `Actions for ${bill.name}`
  const showInline = inline && payable

  if (items.length === 0 && !showInline) return null

  return (
    <span className="rec-actions">
      {showInline && (
        <Button variant="outline" size="panel" aria-label={`Mark paid, ${bill.name}`} onClick={() => onOpenSheet('markPaid', payPeriodOf(bill, entry))}>
          Mark paid
        </Button>
      )}
      {items.length > 0 && (
        <span className="rec-menu" ref={rootRef}>
          <Button
            ref={triggerRef}
            variant="chip"
            size="panel"
            className="rec-menu__trigger"
            aria-haspopup="menu"
            aria-expanded={open}
            aria-label={label}
            onClick={(event) => {
              setHost(event.currentTarget.closest<HTMLElement>('[role="dialog"], [role="alertdialog"]') ?? document.body)
              setOpen((o) => !o)
            }}
          >
            <Icon glyph={Ellipsis} />
          </Button>
          {open &&
            host &&
            createPortal(
            <div
              ref={panelRef}
              className={`rec-menu__panel${phone ? ' rec-menu__panel--phone' : ''}`}
              onKeyDown={onMenuKeyDown}
              // Coordinates and the height cap only, as custom properties; recurring.css reads them.
              style={
                {
                  '--menu-right': `${place?.right ?? 0}px`,
                  '--menu-top': place?.top === undefined ? 'auto' : `${place.top}px`,
                  '--menu-bottom': place?.bottom === undefined ? 'auto' : `${place.bottom}px`,
                  '--menu-max-h': place?.maxHeight === undefined ? 'none' : `${place.maxHeight}px`,
                } as CSSProperties
              }
              data-placed={place ? '' : undefined}
            >
              <span className="rec-menu__reserve rec-menu__reserve--top" aria-hidden="true" />
              <span className="rec-menu__reserve rec-menu__reserve--bottom" aria-hidden="true" />
              <div className="rec-menu__list" role="menu" aria-label={label}>
                {items.map((item, i) => {
                  const blocked = item.disabledReason !== undefined || (busy && item.lifecycle === true)
                  return (
                    <Button
                      key={item.key}
                      ref={(el) => {
                        itemRefs.current[i] = el
                      }}
                      role="menuitem"
                      tabIndex={-1}
                      variant="chip"
                      size="panel"
                      className="rec-menu__item"
                      // aria-disabled, not disabled: it stays in the arrow-key
                      // order so its reason is read when it is reached.
                      aria-disabled={blocked ? true : undefined}
                      aria-describedby={item.disabledReason ? reasonIdOf(item.key) : undefined}
                      onClick={() => {
                        if (blocked) return
                        close()
                        item.run()
                      }}
                    >
                      {item.label}
                    </Button>
                  )
                })}
              </div>
              {/* A deliberate local copy of DisabledReason's shape (adjacent text,
                  never a tooltip): its wrapper div cannot sit inside role="menu",
                  which allows only menuitem, group and separator children. The
                  note follows the list and each item points at its own. */}
              {items.map(
                (item) =>
                  item.disabledReason && (
                    <span key={item.key} id={reasonIdOf(item.key)} role="note" className="note rec-menu__reason">
                      {item.disabledReason}
                    </span>
                  ),
              )}
            </div>,
            host,
          )}
        </span>
      )}
    </span>
  )
}
